import React from "react";
import { PlayIcon, UserIcon } from "../../Icons";
import {
  categoryAccent,
  formatRelativeUpdated,
  youtubeThumbnailUrl,
} from "./trainingCatalog";

/**
 * Training library card — Guidde-style scan layout:
 * gradient cover + title, category/duration pills, updated meta, author footer.
 */
const TrainingVideoCard = ({ video, onPlay }) => {
  const thumb =
    video.thumbnailUrl || youtubeThumbnailUrl(video.youtubeId) || "";
  const updatedLabel = formatRelativeUpdated(video.publishedAt);
  const accent = categoryAccent(video.category);
  const coverTitle = video.shortTitle || video.title;

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md">
      <button
        type="button"
        onClick={() => onPlay?.(video)}
        className="relative block w-full aspect-[16/10] overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2"
        aria-label={`Play ${video.title}`}
      >
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className="absolute inset-0 h-full w-full object-cover scale-105 opacity-40 transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            decoding="async"
          />
        ) : null}

        <span
          className={`absolute inset-0 bg-gradient-to-br ${accent.gradient}`}
          aria-hidden
        />
        <span
          className="absolute -right-6 -top-8 h-28 w-28 rounded-full bg-white/15 blur-[1px]"
          aria-hidden
        />
        <span
          className="absolute right-6 top-4 h-16 w-16 rounded-full bg-white/10"
          aria-hidden
        />

        <span className="absolute inset-0 flex items-center justify-center px-4 py-6">
          <span className="text-center text-[15px] sm:text-base font-bold leading-snug tracking-tight text-white drop-shadow-sm line-clamp-3">
            {coverTitle}
          </span>
        </span>

        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100 bg-slate-950/15">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-indigo-700 shadow-lg">
            <PlayIcon size={22} className="ml-0.5" />
          </span>
        </span>

        <span className="absolute bottom-2.5 left-2.5 right-2.5 flex items-end justify-between gap-2">
          {video.categoryLabel ? (
            <span
              className={`inline-flex max-w-[70%] items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm ${accent.pill}`}
            >
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-white/90"
                aria-hidden
              />
              <span className="truncate">{video.categoryLabel}</span>
            </span>
          ) : (
            <span />
          )}
          {video.durationLabel ? (
            <span className="shrink-0 rounded-md bg-slate-950/75 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white backdrop-blur-sm">
              {video.durationLabel}
            </span>
          ) : null}
        </span>
      </button>

      <div className="flex flex-1 flex-col gap-1 px-3.5 pt-3 pb-3.5">
        <h3 className="text-[13px] font-semibold text-slate-900 leading-snug line-clamp-2">
          {video.title}
        </h3>
        <p className="text-[11px] text-slate-500">
          {updatedLabel ? `Updated ${updatedLabel}` : "Training video"}
        </p>

        <div className="mt-auto flex items-center gap-2 pt-2.5 border-t border-slate-100">
          <span
            className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white ${accent.avatar}`}
            aria-hidden
          >
            <UserIcon size={12} />
          </span>
          <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-slate-700">
            Kornivus Training
          </span>
          <span
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 group-hover:text-indigo-600 transition-colors"
            title="Open video"
            aria-hidden
          >
            <PlayIcon size={14} />
          </span>
        </div>
      </div>
    </article>
  );
};

export default React.memo(TrainingVideoCard);
